import { LoadingButton } from '@mui/lab';
import { Input } from 'components/SignInput';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { changePassword } from 'services/clients/user';

interface Props {
  onSuccess: () => void;
}

interface FormValues {
  password: string;
  passwordConfirmation: string;
  token: string;
}

export const Form: React.FC<Props> = (props) => {
  const changePasswordMutation = useMutation(changePassword);

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || undefined;
  const [otherError, setOtherError] = React.useState('');

  const { register, formState, handleSubmit, watch, setError } = useForm<FormValues>({
    mode: 'all',
    defaultValues: { token },
  });

  register('token', { required: true });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await changePasswordMutation.mutateAsync(values);
      props.onSuccess();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error(error);
      if (error.password) {
        error.password.forEach((err: string) => {
          setError('password', { type: err, message: err });
        });
      }
      if (error.other) {
        setOtherError(error.other);
      }
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-8 w-full max-w-[500px]">
      <Input
        type="password"
        label="Password*"
        placeholder="Enter a strong password (8 characters minimum)"
        inputProps={register('password', {
          required: true,
          deps: ['passwordConfirmation'],
          validate: {
            min: (v) => (v.length >= 8 ? true : 'Password must be at least 8 characters long.'),
          },
        })}
        error={formState.errors.password}
      />
      <Input
        type="password"
        label="Password*"
        placeholder="Confirm password"
        inputProps={register('passwordConfirmation', {
          required: true,
          validate: {
            match: (value) => (value === watch('password') ? true : 'Passwords do not match.'),
          },
        })}
        error={formState.errors.passwordConfirmation}
      />
      {otherError && <div className="text-red-900">{otherError}</div>}
      <LoadingButton
        fullWidth
        type="submit"
        variant="contained"
        disabled={!formState.isValid}
        loading={formState.isSubmitting}
      >
        Change Password
      </LoadingButton>
    </form>
  );
};
